import type { EffectDefinition, EffectContext } from './types'

interface GlitchBlock {
  x: number
  y: number
  width: number
  height: number
  offsetX: number
  life: number
  maxLife: number
  opacity: number
  color: number
}

interface GlitchLine {
  y: number
  width: number
  speed: number
  opacity: number
  life: number
  maxLife: number
}

interface GlitchState {
  blocks: GlitchBlock[]
  lines: GlitchLine[]
  nextBlockSpawn: number
  nextLineSpawn: number
}

export const glitch: EffectDefinition = {
  id: 'glitch',
  name: 'Glitch',

  init(ctx: EffectContext): GlitchState {
    return {
      blocks: [],
      lines: [],
      nextBlockSpawn: 0,
      nextLineSpawn: 0
    }
  },

  update(state: unknown, ctx: EffectContext) {
    const s = state as GlitchState
    const dt = ctx.deltaTime / 1000

    // Spawn glitch blocks
    s.nextBlockSpawn -= dt
    if (s.nextBlockSpawn <= 0) {
      const count = 1 + Math.floor(Math.random() * 3)
      for (let i = 0; i < count; i++) {
        s.blocks.push({
          x: Math.random() * ctx.width,
          y: Math.random() * ctx.height,
          width: 20 + Math.random() * 150,
          height: 2 + Math.random() * 15,
          offsetX: (Math.random() - 0.5) * 40,
          life: 0.05 + Math.random() * 0.15,
          maxLife: 0.05 + Math.random() * 0.15,
          opacity: 0.05 + Math.random() * 0.12,
          color: Math.floor(Math.random() * 3)
        })
      }
      s.nextBlockSpawn = 0.1 + Math.random() * 0.4
    }

    // Spawn scan lines
    s.nextLineSpawn -= dt
    if (s.nextLineSpawn <= 0) {
      s.lines.push({
        y: -2,
        width: ctx.width,
        speed: 200 + Math.random() * 600,
        opacity: 0.03 + Math.random() * 0.06,
        life: 2,
        maxLife: 2
      })
      s.nextLineSpawn = 0.3 + Math.random() * 1
    }

    // Update blocks
    for (let i = s.blocks.length - 1; i >= 0; i--) {
      s.blocks[i]!.life -= dt
      if (s.blocks[i]!.life <= 0) s.blocks.splice(i, 1)
    }

    // Update lines
    for (let i = s.lines.length - 1; i >= 0; i--) {
      const line = s.lines[i]!
      line.y += line.speed * dt
      line.life -= dt
      if (line.y > ctx.height || line.life <= 0) s.lines.splice(i, 1)
    }
  },

  draw(state: unknown, ctx: EffectContext) {
    const s = state as GlitchState
    const { ctx: c } = ctx

    // Draw blocks
    for (const block of s.blocks) {
      const colors = [
        `rgba(255, 0, 80, ${block.opacity})`,
        `rgba(0, 255, 200, ${block.opacity})`,
        `rgba(80, 120, 255, ${block.opacity})`
      ]
      c.fillStyle = colors[block.color]!
      c.fillRect(block.x + block.offsetX, block.y, block.width, block.height)
    }

    // Draw scan lines
    for (const line of s.lines) {
      c.fillStyle = `rgba(255, 255, 255, ${line.opacity})`
      c.fillRect(0, line.y, line.width, 1)
    }
  }
}
